import { AutomatedContentProps } from "./AutomatedContent.types";

type AutomatedContentEntry = NonNullable<AutomatedContentProps["first_content"]>;
type AutomatedContentResult = AutomatedContentEntry | null;
type PreviewKey = "first" | "second" | "third";

export const getContentToDisplay = (props: AutomatedContentProps): AutomatedContentResult => {
  const { start_date_source, first_content_preview, second_content_preview, third_content_preview } = props;

  if ([first_content_preview, second_content_preview, third_content_preview].some(isPreviewEnabled)) {
    return getPreviewContent(props);
  }
  const current_date = new Date();

  if (start_date_source) {
    return getStartDateFromApi(props, current_date);
  } else {
    // If start_date_source is not set, the custom start date from the first content will be used instead.
    return getCustomStartDate(props, current_date);
  }
};

const getStartDateFromApi = (props: AutomatedContentProps, current_date: Date): AutomatedContentResult => {
  const {
    start_date_source,
    metaStartDate,
    pricingApiStartDate,
    days_before_start_date,
    first_content,
    first_content_duration,
    second_content,
    second_content_duration,
    third_content,
    third_content_duration,
  } = props;

  if (start_date_source === "Program Meta (Product API)" && !metaStartDate) return null;
  if (start_date_source === "Pricing API" && !pricingApiStartDate) return null;

  const programStartDate =
    start_date_source === "Program Meta (Product API)"
      ? new Date(metaStartDate || "")
      : start_date_source === "Pricing API"
        ? new Date(pricingApiStartDate || "")
        : null;

  if (!programStartDate || Number.isNaN(programStartDate.getTime())) {
    return null;
  }

  // Calculate when first content should start
  const firstContentStartDays = days_before_start_date || 0;
  const firstContentStartDate = new Date(programStartDate);
  firstContentStartDate.setDate(programStartDate.getDate() - firstContentStartDays);

  // Check if we're before first content should start
  if (current_date < firstContentStartDate) {
    return null;
  }

  // Calculate days since first content started (not program start)
  const daysSinceFirstContentStart = Math.floor(
    (current_date.getTime() - firstContentStartDate.getTime()) / (1000 * 60 * 60 * 24),
  );

  // First content
  if (first_content && first_content_duration) {
    if (daysSinceFirstContentStart < first_content_duration) {
      return first_content;
    }
  }

  // Second content
  if (second_content && second_content_duration) {
    const secondContentStart = first_content_duration || 0;
    const secondContentEnd = secondContentStart + second_content_duration;
    if (daysSinceFirstContentStart >= secondContentStart && daysSinceFirstContentStart < secondContentEnd) {
      return second_content;
    }
  }

  // Third content
  if (third_content && third_content_duration) {
    const thirdContentStart = (first_content_duration || 0) + (second_content_duration || 0);
    const thirdContentEnd = thirdContentStart + third_content_duration;
    if (daysSinceFirstContentStart >= thirdContentStart && daysSinceFirstContentStart < thirdContentEnd) {
      return third_content;
    }
  }

  return null;
};

const getCustomStartDate = (props: AutomatedContentProps, current_date: Date): AutomatedContentResult => {
  const {
    content_custom_end_date,
    first_content,
    first_content_custom_start_date,
    second_content,
    second_content_custom_start_date,
    third_content,
    third_content_custom_start_date,
  } = props;

  if (content_custom_end_date) {
    if (current_date >= new Date(content_custom_end_date)) {
      return null;
    }
  }

  // Check third content first (most recent)
  if (third_content && third_content_custom_start_date) {
    if (current_date >= new Date(third_content_custom_start_date)) {
      return third_content;
    }
  }

  // Check second content
  if (second_content && second_content_custom_start_date) {
    if (current_date >= new Date(second_content_custom_start_date)) {
      return second_content;
    }
  }

  // Check first content
  if (first_content && first_content_custom_start_date) {
    if (current_date >= new Date(first_content_custom_start_date)) {
      return first_content;
    }
  }

  return null;
};

const getPreviewContent = (props: AutomatedContentProps): AutomatedContentResult => {
  const {
    first_content,
    first_content_preview,
    second_content,
    second_content_preview,
    third_content,
    third_content_preview,
  } = props;

  const previewContent: Array<{ key: PreviewKey; content: AutomatedContentResult }> = [];

  if (isPreviewEnabled(first_content_preview)) {
    previewContent.push({ key: "first", content: first_content ?? null });
  } else if (isPreviewDisabled(first_content_preview)) {
    removePreviewContent(previewContent, "first");
  }

  if (isPreviewEnabled(second_content_preview)) {
    previewContent.push({ key: "second", content: second_content ?? null });
  } else if (isPreviewDisabled(second_content_preview)) {
    removePreviewContent(previewContent, "second");
  }

  if (isPreviewEnabled(third_content_preview)) {
    previewContent.push({ key: "third", content: third_content ?? null });
  } else if (isPreviewDisabled(third_content_preview)) {
    removePreviewContent(previewContent, "third");
  }

  return previewContent.at(-1)?.content ?? null;
};

const isPreviewEnabled = (value?: string): boolean => value?.toLowerCase() === "true";
const isPreviewDisabled = (value?: string): boolean => value?.toLowerCase() === "false";

const removePreviewContent = (
  previewContent: Array<{ key: PreviewKey; content: AutomatedContentResult }>,
  key: PreviewKey
): void => {
  const previewIndex = previewContent.findIndex((previewItem) => previewItem.key === key);

  if (previewIndex >= 0) {
    previewContent.splice(previewIndex, 1);
  }
};
